import type { IScannerControls } from "@zxing/browser";
import classNames from "classnames";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { Button } from "~/common/components/Button/Button";
import Modal from "~/common/components/Modal";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import classes from "./DMCScanPage.module.less";
import CustomModalHeader from "~/ScanDMC/components/CustomModalHeader/CustomModalHeader";
import { Trans, useTranslation } from "react-i18next";
import { IconSize } from "~/common/components/icon/IconSize";
import { IconButton } from "@mui/material";
import { CircularProgress } from "~/common/components/CircularProgress";
import { type OriginCheckResult, ScanResultType } from "~/ScanDMC/models/ScanDMC";
import { fetchScanDMCOriginCheck } from "~/ScanDMC/api/fetchScanDMCOriginCheck";
import { trackDmcScan } from "~/feature/Analytics/ga/ga";
import { useDMCScanner } from "~/ScanDMC/pages/DMCScanPage/DMCScanner/useDMCScanner";
import CenterContents from "~/common/components/CenterContents";
import { BackLink, LinkButton } from "~/common/components/Link";
import { IMG_UPLOAD_IMAGE } from "~/utils/url/staticResources";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  onScanResult: (result: OriginCheckResult) => Promise<void>;
  onShowHelp: () => void;
}

export const DMCScanPage = (props: Props) => {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const { val: modalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [scannedBefore, setScannedBefore] = useState(false);

  const { loading: loadingScanner, scanner } = useDMCScanner();

  const [isDecodingImage, setIsDecodingImage] = useState(false);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);

  const closeModalCb = () => {
    closeModal();
    setScanned(false);
  };

  const toggleTorch = () => {
    if (!controlsRef.current?.switchTorch) {
      return;
    }
    controlsRef.current.switchTorch(!torchEnabled);
    setTorchEnabled(!torchEnabled);
  };

  const scanDMCCode = async (text: string) => {
    // It's an outer packaging
    if (isOuterPackagingCode(text)) {
      trackDmcScan(ScanResultType.OuterPackaging, text);
      openModal();
      return;
    }

    // Not outer packaging, let's ask the server
    setLoading(true);

    try {
      const result = await fetchScanDMCOriginCheck(text);

      const { type, dmcCode, productCode } = result;
      trackDmcScan(type, dmcCode, productCode);
      await props.onScanResult(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!scanner || !video.current || scanned) {
        return;
      }

      let decodePromise: Promise<string | null | undefined>;
      if (scannedBefore) {
        decodePromise = scanner.decodeOnce(video.current);
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1920 },
              // frameRate: { ideal: 30 },
              facingMode: "environment",
            },
          });

          scanner.reset();
          decodePromise = scanner.attachStreamToVideo(stream, video.current!).then((el) => {
            setShowCamera(true);
            return scanner.decodeOnce(el);
          });
        } catch (e: unknown) {
          setIsPermissionDenied(true);
          return;
        }
      }
      decodePromise.then((code) => {
        if (code) {
          setScanned(true);
          setScannedBefore(true);
          scanDMCCode(code);
        }
      });
    })();
  }, [scanner, video.current, scanned]);

  const fromFileCb = () => {
    if (!scanner || !fileInputRef.current) {
      return;
    }
    fileInputRef.current.click();
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      try {
        setIsDecodingImage(true);
        const result = await scanner?.decodeFromFile(file);
        if (result) {
          scanDMCCode(result);
        }
      } catch (e: unknown) {
        props.onScanResult({ type: ScanResultType.NoCodeDetected, dmcCode: "" });
      } finally {
        setIsDecodingImage(false);
      }
    }
  };

  const footer = (
    <div className={classes.scanFooter}>
      {torchAvailable && (
        <IconButton onClick={toggleTorch} color={torchEnabled ? "primary" : "secondary"}>
          <Icon type={torchEnabled ? IconType.FlashlightOn : IconType.FlashlightOff} size={IconSize.FONT_24} />
        </IconButton>
      )}

      <IconButton onClick={props.onShowHelp} color={"primary"}>
        <Icon type={IconType.InfoCircleOutline} size={IconSize.FONT_24} />
      </IconButton>
    </div>
  );

  const showLoading = loading || loadingScanner || isDecodingImage;

  return (
    <>
      {modalOpen && (
        <Modal
          hideClose
          onClose={closeModalCb}
          modalClassName={classNames(classes.outerPackagingModal, "scha__modal__contents")}
        >
          <div className={classes.buttons}>
            <Alert severity={"info"}>
              <Trans i18nKey="page.scandmc.message.outerPackaging" />
            </Alert>

            <Button onClick={closeModalCb}>{t("page.scandmc.form.button.close")}</Button>
          </div>
        </Modal>
      )}
      <Modal
        headerOverride={<CustomModalHeader />}
        modalClassName="scha__modal__full-screen-view scha__modal--scanner"
        onClose={() => undefined}
        footerChildren={!showLoading ? footer : undefined}
      >
        {showLoading && (
          <CenterContents className={"w-100"}>
            <CircularProgress className={classes.loading} size={64} />
          </CenterContents>
        )}
        {!showLoading && (
          <div className={classes.root}>
            <BackLink className={"mb-30"} iconSize={IconSize.FONT_24} href={HOMEPAGE}>
              {t("navigation.home")}
            </BackLink>
            <input type={"file"} ref={fileInputRef} onChange={onFileChange} hidden={true} />

            <div className={classNames(classes.content, isPermissionDenied && classes.centerContent)}>
              {!isPermissionDenied && (
                <>
                  <div className={classes.videoContainer}>
                    <video
                      ref={video}
                      className={classes.video}
                      style={{ visibility: showCamera ? "visible" : "hidden" }}
                    />
                    <div className={classes.videoOverlay}>
                      <svg viewBox="0 0 100 100">
                        <path d="M25,2 L2,2 L2,25" />
                        <path d="M2,75 L2,98 L25,98" />
                        <path d="M75,98 L98,98 L98,75" />
                        <path d="M98,25 L98,2 L75,2" />
                      </svg>
                    </div>
                  </div>
                  <span className={classes.or}>{t("page.scandmc.or")}</span>
                  <LinkButton
                    iconBefore={
                      <>
                        <CircularProgress size={20} hidden={!isDecodingImage} />
                        <Icon type={IconType.FilePlus} hidden={isDecodingImage} />
                      </>
                    }
                    disabled={isDecodingImage}
                    onClick={fromFileCb}
                  >
                    {t("page.scandmc.button.uploadImage")}
                  </LinkButton>
                </>
              )}

              {isPermissionDenied && (
                <>
                  <img src={IMG_UPLOAD_IMAGE} alt={""} />
                  <Button
                    startIcon={<Icon type={IconType.FilePlus} />}
                    disabled={isDecodingImage}
                    onClick={fromFileCb}
                    fullWidth
                  >
                    {t("page.scandmc.button.uploadImage")}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

const OUTER_PACKAGING_CODES = ["02", "\u001d02"];

function isOuterPackagingCode(code: string) {
  return OUTER_PACKAGING_CODES.some((prefix) => code.startsWith(prefix));
}
